import axios from 'axios';
import { getRuntimeConfig } from '../config/runtimeConfig';

function isAccessTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

function isPublicInteractiveEvaluationPath() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return path.startsWith('/public/evaluation/') || path.startsWith('/print/evaluation-copy/');
}

function isCreatorModeValidationPath() {
  if (typeof window === 'undefined') return false;
  return (window.location.pathname || '').startsWith('/validate-creator-mode/');
}

/** Activer les logs détaillés par requête : localStorage.setItem('AQIBOOST_VERBOSE_AXIOS','1') puis F5 */
const verboseAxios =
  typeof window !== 'undefined' &&
  window.localStorage?.getItem('AQIBOOST_VERBOSE_AXIOS') === '1';

// Créer une instance d'axios qui sera initialisée après le chargement de la config
let api = null;
let isInitialized = false;

// Fonction pour initialiser Axios avec la configuration runtime
export const initializeAxios = () => {
  if (isInitialized) {
    if (verboseAxios) {
      console.log('[Axios Config] Axios déjà initialisé');
    }
    return api;
  }

  const config = getRuntimeConfig();
  const apiBaseURL = config.REACT_APP_API_BASE_URL;

  if (verboseAxios) {
    console.log('[Axios Config] apiBaseURL (runtime):', apiBaseURL);
  }

  // Créer une instance d'axios avec une configuration de base
  api = axios.create({
    baseURL: apiBaseURL,
    headers: {
      'Accept': 'application/json'
    },
    withCredentials: true, // Important pour les cookies d'authentification
    // Autoriser les redirections pour éviter des erreurs 307 traitées comme des échecs
    maxRedirects: 5,
    // Timeout global généreux (peut être surchargé par requête)
    timeout: 300000,
  });

  // Intercepteur pour ajouter le token d'authentification
  api.interceptors.request.use(
  (config) => {
    if (String(config.url || '').includes('establishment-space')) {
      const establishmentToken = localStorage.getItem('establishmentToken');
      if (establishmentToken && !isAccessTokenExpired(establishmentToken)) {
        config.headers.Authorization = `Bearer ${establishmentToken}`;
      } else {
        if (establishmentToken) localStorage.removeItem('establishmentToken');
        delete config.headers.Authorization;
      }
      return config;
    }

    const token = localStorage.getItem('accessToken');
    if (verboseAxios) {
      console.log('[Axios Request]', config.method, config.url, 'token:', !!token);
    }

    if (token) {
      if (isAccessTokenExpired(token)) {
        console.warn('[Axios Request Interceptor] Token has expired');
        localStorage.removeItem('accessToken');
        if (
          isCreatorModeValidationPath()
          || isPublicInteractiveEvaluationPath()
          || window.location.pathname.includes('/login')
        ) {
          return config;
        }
        window.location.href = '/login';
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      if (verboseAxios) {
        console.warn('[Axios Request] Pas de token — requête sans Authorization');
      }
      // NE PLUS REDIRIGER ICI AUTOMATIQUEMENT.
      // Laisser l'API répondre par un 401 si l'authentification est requise,
      // et l'intercepteur de réponse gérera la redirection.
      // Les routes publiques fonctionneront sans token.
    }

    if (verboseAxios) {
      try {
        console.log('[Axios Request] headers:', { ...config.headers, Authorization: config.headers.Authorization ? '(présent)' : undefined });
      } catch (e) {
        console.warn('[Axios Request] headers non sérialisables');
      }
      try {
        console.log('[Axios Request] data:', config.data);
      } catch (e) {
        console.warn('[Axios Request] data non sérialisable');
      }
    }

    return config;
  },
  (error) => {
    console.error('[Axios Request Interceptor] Error:', error);
    return Promise.reject(error);
  }
);

// Intercepteur de réponse pour le débogage et la gestion des erreurs
api.interceptors.response.use(
  (response) => {
    if (verboseAxios) {
      console.log('[Axios Response]', response.config?.url, response.status);
    }
    return response;
  },
  (error) => {
    const originalRequest = error.config;

    if (verboseAxios) {
      console.error('[Axios Response] Erreur:', {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
    } else if (error.response) {
      const d = error.response.data;
      console.error(
        '[Axios Response]',
        error.response.status,
        originalRequest?.method,
        originalRequest?.url,
        d !== undefined && d !== '' ? d : ''
      );
    } else {
      console.error('[Axios Response] Erreur réseau ou sans réponse:', error.message);
    }
    
    // 401 : ne déconnecter que si le JWT est absent ou expiré.
    // Un 401 avec token encore valide vient souvent d'une redirection 307
    // (slash final) qui fait perdre le header Authorization en cross-origin.
    if (error.response?.status === 401 && String(originalRequest?.url || '').includes('establishment-space')) {
      localStorage.removeItem('establishmentToken');
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const token = localStorage.getItem('accessToken');
      if (token && !isAccessTokenExpired(token)) {
        console.warn(
          '[Axios Response Interceptor] 401 avec session encore valide — déconnexion évitée',
          originalRequest?.url
        );
      } else if (!isPublicInteractiveEvaluationPath() && !isCreatorModeValidationPath()) {
        console.warn('[Axios Response Interceptor] 401 Unauthorized - Redirecting to login');
        localStorage.removeItem('accessToken');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    
    // Pour les autres types d'erreurs, on peut ajouter une gestion spécifique ici
    if (error.response?.status === 403) {
      console.warn('[Axios Response] 403 Forbidden');
    } else if (error.response?.status === 404) {
      console.warn('[Axios Response] 404 Not Found:', originalRequest?.url);
    } else if (error.response?.status >= 500) {
      console.error('[Axios Response Interceptor] Server Error - Please try again later');
    }
    
    return Promise.reject(error);
  }
  );

  isInitialized = true;
  return api;
};

export const getApi = () => {
  if (!api) {
    throw new Error('Axios non initialisé. Appelez initializeAxios() d\'abord.');
  }
  return api;
};

// Créer un objet qui se comporte comme Axios mais initialise lazy
// Ne pas utiliser de Proxy car il s'exécute au module load time
class AxiosLazyWrapper {
  constructor() {
    this._initialized = false;
  }

  _ensureInitialized() {
    if (!this._initialized && isInitialized) {
      this._initialized = true;
    }
  }

  get(url, config) {
    return getApi().get(url, config);
  }

  post(url, data, config) {
    return getApi().post(url, data, config);
  }

  put(url, data, config) {
    return getApi().put(url, data, config);
  }

  patch(url, data, config) {
    return getApi().patch(url, data, config);
  }

  delete(url, config) {
    return getApi().delete(url, config);
  }

  request(config) {
    return getApi().request(config);
  }

  head(url, config) {
    return getApi().head(url, config);
  }

  options(url, config) {
    return getApi().options(url, config);
  }

  get interceptors() {
    return getApi().interceptors;
  }

  get defaults() {
    return getApi().defaults;
  }

  create(config) {
    return getApi().create(config);
  }
}

export default new AxiosLazyWrapper();
